import { api } from 'api/api';
import { useContext, useEffect, useState, createContext, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTelemedicina } from 'context/TelemedicinaContext';
import { AccountContext } from 'components/AccountContext';
import { userIsSubscriber } from 'components/Utils/theme';

const ENDPOINT =
  process.env.REACT_APP_ENVIRONMENT === 'production'
    ? process.env.REACT_APP_BASE_URL_API_SERVER_PROD
    : process.env.REACT_APP_BASE_URL_API_SERVER_TEST;

export const AgendamentoContext = createContext();

const useModelMedicos = () => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [especialista, setEspecialista] = useState(null);
  const [modalEspecialista, setModalEspecialista] = useState(false);
  const [avaliacaoMedia, setAvaliacaoMedia] = useState(null);
  const navigate = useNavigate();
  const { paciente, setPaciente, agendamento, atualizarAgendamento, setMedicoSelecionado, setAgendamento } = useTelemedicina();
  const { user, toastInadimplente } = useContext(AccountContext);
  const [medicos, setMedicos] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [avaliacoes_atualizadas, setAvaliacoesAtualizadas] = useState([]);
  const [proximasDatas, setProximasDatas] = useState([]);
   const assinante = userIsSubscriber(user);
  const [loadingAgendas, setLoadingAgendas] = useState(true);

  // Paciente padrão para usuários deslogados
  const pacientePadrao = useMemo(() => ({
    sexo: 'M',
    idade: 25,
    cpf: null
  }), []);

  const pacienteAtual = paciente || (user?.loggedIn ? null : pacientePadrao);

  // Quem chega aqui via deeplink (notificação) pode ter o "user" do AccountContext ainda
  // carregando no momento da navegação, então setPaciente antes de navegar (useRoutes.js) pode
  // rodar cedo demais e não pegar user.loggedIn/cpf a tempo. Este efeito é reativo: roda de novo
  // sempre que os dados do usuário mudarem, então se auto-corrige assim que o user fica pronto.
  useEffect(() => {
    let ignore = false;
    async function garantirPacienteLogado() {
      if (paciente || !user?.loggedIn || !user?.cpf) return;
      try {
        const titular = user.titular;
        const id_contrato = titular ? user?.id_contrato : user?.id_contrato_dependent?.paciente_contrato_id;
        const response = await api(`${ENDPOINT}/pacientes/${titular}/${assinante}/${id_contrato}/${user.cpf}`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          method: 'GET',
        });
        const dependentes = await response.json();
        const pacienteLogado = Array.isArray(dependentes) ? dependentes.find((p) => p && p.cpf === user?.cpf) : null;
        if (!ignore && pacienteLogado) {
          setPaciente(pacienteLogado);
        }
      } catch (error) {
        // silencioso: se falhar, o fallback de deslogado ainda deixa a tela utilizável
      }
    }
    garantirPacienteLogado();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.loggedIn, user?.cpf, user?.titular, user?.id_contrato, user?.id_contrato_dependent?.paciente_contrato_id, paciente, assinante]);

  const listarEspecialidades = useCallback(async () => {
    try {
      setLoading(true);
      const sexoFeminino = pacienteAtual?.sexo === 'F' || pacienteAtual?.sexo === 'f' ? true : false;
      const contratoId = user?.id_contrato || 0; 
      const idade = pacienteAtual?.idade || 18; 
      
      const url = ENDPOINT + `/schedule/list-specialities/${sexoFeminino}/${contratoId}/${idade}`;
      const response = await api(url, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'GET',
      });
      const postsData = await response.json();
      setEspecialista(postsData);
    } catch (error) {
      setError('Não foi possível carregar as especialidades.');
    } finally {
      setLoading(false);
    }
  }, [pacienteAtual?.sexo, pacienteAtual?.idade, user?.id_contrato]);

  useEffect(() => {
    let ignore = false;
    if (!ignore && pacienteAtual && (user?.loggedIn !== false || pacienteAtual === pacientePadrao)) {
      listarEspecialidades();
    }
    return () => {
      ignore = true;
    };
  }, [pacienteAtual, listarEspecialidades, user?.loggedIn, pacientePadrao]);

  async function fetchData(url) {
    try {
      let response = await api(url, {
        headers: { 'Content-Type': 'application/json' },
        method: 'GET',
      });
      let data = await response.json();
      return data;
    } catch (error) {
      return [];
    }
  }

  const fetchAvaliacoes = useCallback(async (medicos) => {
    if (!medicos) return [];
    const medicos_id = medicos.map((medico) => medico.dados.medico_id);
    const response = await api(`${ENDPOINT}/schedule/getAvaliacoesMedicos`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({ medicos_id }),
    });
    await response.json().then(async function (postsData) {
      if (!postsData) return [];
      setAvaliacoesAtualizadas(postsData);
    });
  }, []);

  const fetchProximasData = useCallback(async () => {
    try {
      // Apenas busca próximas datas se há usuário logado e paciente
      if (!user?.loggedIn || !pacienteAtual?.cpf) {
        setProximasDatas([]);
        setLoading(false);
        return;
      }
      
      const response = await api(`${ENDPOINT}/schedule/searchProximasDatas`, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({
          username: pacienteAtual.cpf,
          especialidadeId: agendamento?.especialidadeId,
          paciente_id: user.id_cliente_deovita,
        }),
      });
      await response.json().then(async function (postsData) {
        setLoading(false);
        if (!postsData) {
          setProximasDatas([]);
          return [];
        }
        setAgendamento((prev) => ({
          ...prev,
          atendimento_gratuito: postsData?.telemedicina?.telemedicina_especialista_gratis,
          faltou: postsData.falta,
          plano_id: postsData?.telemedicina?.plano_id,
          pronto_atendimento_gratis: postsData?.telemedicina?.pronto_atendimento_gratis,
          telemedicina_especialista_gratis: postsData?.telemedicina?.telemedicina_especialista_gratis,
          especialidades: postsData?.especialidades,
          especialista: true,
        }));
        const allDatas = Object.values(postsData.data || {}).flat();
        setProximasDatas(allDatas);
      });
    } catch (error) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [user?.loggedIn, pacienteAtual?.cpf, agendamento?.especialidadeId, user?.id_cliente_deovita, setAgendamento]);

  useEffect(() => {
    async function fetchInitialMedicos() {
      // Só busca médicos se há um paciente válido ou se for primeira chamada para usuário deslogado
      if (!pacienteAtual) {
        setLoading(false);
        return;
      }

      setLoading(true);
      let urlPart;
      if (user?.coords != null) {
        urlPart = `&lat=${user.coords.latitudeAtual}&lng=${user.coords.longitudeAtual}`;
      } else {
        if (
          user?.coordenadas_cidade &&
          user.coordenadas_cidade.latitude !== null &&
          user.coordenadas_cidade.latitude !== 'null' &&
          user.coordenadas_cidade.latitude !== undefined &&
          user.coordenadas_cidade.latitude !== 'undefined'
        ) {
          urlPart = `&lat=${user.coordenadas_cidade.latitude}&lng=${user.coordenadas_cidade.longitude}`;
        } else {
          urlPart = `&city=${user?.cidade ? user.cidade : 'FORTALEZA'}`;
        }
      }
      
      // Valores padrão para usuários deslogados
      const sexoPadrao = pacienteAtual?.sexo || 'M'; // Padrão masculino se não especificado
      
      let url = ENDPOINT + `/schedule/searchMedicos?page=1&limit=10${urlPart}&profession=Todos&typeList=Especialista&sexo=${sexoPadrao}${agendamento.especialidadeId ? `&especialidade_id=${agendamento.especialidadeId}` : ''}${user.id_plano ? `&plano_id=${user.id_plano}` : ''}`;
      let data = await fetchData(url);
      setMedicos(data);
      setPage(1);
      setHasMore(data.length > 0);
      if (data.length === 0) {
        setLoading(false);
        return;
      }

      await fetchAvaliacoes(data);
      setLoading(false);
      setLoadingAgendas(false);
      
      // Só busca próximas datas se usuário logado
      if (user?.loggedIn && pacienteAtual?.cpf) {
        await fetchProximasData();
      }
    }
    fetchInitialMedicos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agendamento?.especialidadeId, pacienteAtual?.cpf, pacienteAtual?.sexo]);

  const busyStackRef = useRef(false);
  const debounceTimeout = useRef(null);
  useEffect(() => {
    const handleScroll = () => {
      if (!hasMore || loading || busyStackRef.current) return;
      if (window.innerHeight + window.scrollY + 10 > document.body.offsetHeight) {
        busyStackRef.current = true;
        clearTimeout(debounceTimeout.current);
        debounceTimeout.current = setTimeout(async () => {
          let urlPart;
          if (user?.coords != null) {
            urlPart = `&lat=${user.coords.latitudeAtual}&lng=${user.coords.longitudeAtual}`;
          } else {
            if (
              user?.coordenadas_cidade &&
              user.coordenadas_cidade.latitude !== null &&
              user.coordenadas_cidade.latitude !== 'null' &&
              user.coordenadas_cidade.latitude !== undefined &&
              user.coordenadas_cidade.latitude !== 'undefined'
            ) {
              urlPart = `&lat=${user.coordenadas_cidade.latitude}&lng=${user.coordenadas_cidade.longitude}`;
            } else {
              urlPart = `&city=${user?.cidade ? user.cidade : 'FORTALEZA'}`;
            }
          }
          let nextPage = page + 1;
          const sexoPadrao = pacienteAtual?.sexo || 'M';
          let url =
            ENDPOINT + `/schedule/searchMedicos?page=${nextPage}&limit=10${urlPart}&profession=Todos&typeList=Especialista&sexo=${sexoPadrao}${agendamento.especialidadeId ? `&especialidade_id=${agendamento.especialidadeId}` : ''}`;
          let data = await fetchData(url);
          setHasMore(data.length > 0);
          if (data.length === 0) {
            setLoading(false);
            busyStackRef.current = false;
            return;
          }
          setMedicos((prev) => {
            const prevIds = new Set(prev.map((m) => m.dados.medico_id));
            const novosMedicos = data.filter((m) => !prevIds.has(m.dados.medico_id));
            if (novosMedicos.length > 0) {
              fetchAvaliacoes(novosMedicos);
            }
            return [...prev, ...novosMedicos];
          });
          setPage(nextPage);
          setLoading(false);
          busyStackRef.current = false;
        }, 300);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      busyStackRef.current = false;
      clearTimeout(debounceTimeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agendamento?.especialidadeId, page, hasMore, loading]);


  const filteredMedicos = useMemo(() => {
    if (!agendamento?.especialidadeId) {
      return medicos;
    }
    return medicos.filter((prop) => String(prop?.dados?.ambulatorio_tipo_consulta_id) === String(agendamento?.especialidadeId));
  }, [medicos, agendamento?.especialidadeId]);

  return {
    error,
    loading,
    especialista,
    modalEspecialista,
    setModalEspecialista,
    navigate,
    agendamento,
    atualizarAgendamento,
    avaliacaoMedia,
    setAvaliacaoMedia,
    medicos: filteredMedicos,
    avaliacoes_atualizadas,
    assinante,
    setMedicoSelecionado,
    user: user || { loggedIn: false },
    toastInadimplente: toastInadimplente || (() => {}),
    proximasDatas,
    loadingAgendas,
  };
};

export default useModelMedicos;
